import type { Buffer } from "./model/buffer";
import type { Cursor } from "./model/cursor";
import type { SearchDirection } from "./model/editor-state";
import { classAt, stepBackward, stepForward } from "./motions/word-class";

function comparePositions(a: Cursor, b: Cursor): number {
  return a.line - b.line || a.col - b.col;
}

function findAllMatches(buffer: Buffer, query: string): Cursor[] {
  if (query === "") {
    return [];
  }
  const matches: Cursor[] = [];
  buffer.lines.forEach((line, lineIndex) => {
    let fromIndex = 0;
    for (;;) {
      const idx = line.indexOf(query, fromIndex);
      if (idx === -1) {
        break;
      }
      matches.push({ line: lineIndex, col: idx });
      fromIndex = idx + 1;
    }
  });
  return matches;
}

/**
 * リテラル一致で次の一致位置を探す（正規表現は使わない — ReDoS経路を作らないため）。
 * 見つからなければ折り返して先頭/末尾から再検索する（wrapscan）。全く見つからなければnull。
 */
export function findNextMatch(
  buffer: Buffer,
  cursor: Cursor,
  query: string,
  direction: SearchDirection,
): Cursor | null {
  const matches = findAllMatches(buffer, query);
  if (matches.length === 0) {
    return null;
  }
  if (direction === "forward") {
    const after = matches.find((m) => comparePositions(m, cursor) > 0);
    return after ?? (matches[0] as Cursor);
  }
  const before = [...matches].reverse().find((m) => comparePositions(m, cursor) < 0);
  return before ?? (matches[matches.length - 1] as Cursor);
}

/** カーソル位置の単語を取得する（`*` `#` 用）。単語上になければ次の単語を探す。 */
export function wordUnderCursor(buffer: Buffer, cursor: Cursor): string | null {
  let start: Cursor | null = cursor;
  if (classAt(buffer, start) !== "word") {
    while (start !== null && classAt(buffer, start) !== "word") {
      start = stepForward(buffer, start);
    }
  }
  if (start === null) {
    return null;
  }

  let left = start;
  for (;;) {
    const prev = stepBackward(buffer, left);
    if (prev === null || prev.line !== left.line || classAt(buffer, prev) !== "word") {
      break;
    }
    left = prev;
  }

  let right = start;
  for (;;) {
    const next = stepForward(buffer, right);
    if (next === null || next.line !== right.line || classAt(buffer, next) !== "word") {
      break;
    }
    right = next;
  }

  const line = buffer.lines[left.line] ?? "";
  return line.slice(left.col, right.col + 1);
}
